import { doc, getDoc } from 'firebase/firestore';
import { db } from "../firebase/config";
import { SPORTS_COACH_SYSTEM_INSTRUCTION } from "../constants/systemInstructions";
import { AiContent, callAi } from "./aiClient";

const CHAT_MODEL = 'gemini-2.5-flash';

export interface Message {
    id?: string;
    role: 'user' | 'model';
    content: string;
    timestamp: Date;
}

export class GeminiService { 
    private chat: AiContent[] | null;
    private baseSystemInstruction = SPORTS_COACH_SYSTEM_INSTRUCTION;
    private currentSystemInstruction = SPORTS_COACH_SYSTEM_INSTRUCTION;
    private userId?: string;

    constructor(userId?: string) {
        this.userId = userId;
        this.chat = null;
    }

    // Fetch custom tone settings from Firestore
    private async fetchCustomTone(userId: string): Promise<string | null> {
        try {
            const toneDoc = await getDoc(doc(db, 'systemInstructions', userId, 'tone', 'tone_1'));
            
            if (toneDoc.exists()) {
                const data = toneDoc.data();
                if (data.enabled && data.toneText && data.toneText.trim()) {
                    return data.toneText.trim();
                }
            }
            return null;
        } catch (error) {
            console.error('Error fetching custom tone:', error);
            return null;
        }
    }

    private async loadCustomToneAndUpdateInstructions() {
        if (!this.userId) {
            this.currentSystemInstruction = this.baseSystemInstruction;
            return;
        }
        
        const customTone = await this.fetchCustomTone(this.userId);
        
        if (customTone) {
            const customToneSection = `
                ## CUSTOM TONE OVERRIDE (PRIORITY INSTRUCTION)
                The user has specified custom tone preferences that supersede all other communication style guidelines:

                ${customTone}

                This custom tone instruction takes precedence over the "Communication Style & Tone" section above. Apply these preferences consistently while maintaining the core expertise and safety protocols.
            `;
            
            this.currentSystemInstruction = this.baseSystemInstruction.replace(
                '## Key Capabilities & Expertise Areas',
                customToneSection + '## Key Capabilities & Expertise Areas'
            );
            
            console.log('✅ Custom tone loaded and applied to system instructions');
        } else {
            this.currentSystemInstruction = this.baseSystemInstruction;
        }
    }

    async refreshCustomTone() {
        this.chat = null;
        await this.loadCustomToneAndUpdateInstructions();
        console.log('🔄 Custom tone refreshed');
    }

    // The conversation is kept here and sent in full with each message,
    // because aiProxy doesn't hold chat state.
    async startChat(history: Message[] = []) {
        console.log('🚀 GeminiService.startChat called with history');

        await this.loadCustomToneAndUpdateInstructions();

        this.chat = history.map((msg): AiContent => ({
            role: msg.role,
            parts: [{ text: msg.content }],
        }));
        
        console.log('✅ Chat session initialized');
    }

    async sendMessage(message: string): Promise<string> {
        try {
            if (!this.chat) {
                await this.startChat([]);
            }

            // The server checks the allowance and records the usage.
            console.log('📤 Sending message to Gemini...');
            const userTurn: AiContent = { role: 'user', parts: [{ text: message }] };
            const result = await callAi({
                feature: 'chat',
                model: CHAT_MODEL,
                contents: [...(this.chat ?? []), userTurn],
                systemInstruction: this.currentSystemInstruction,
                generationConfig: {
                    maxOutputTokens: 4096,
                    temperature: 0.7,
                    thinkingBudget: 0,
                },
            });
            const responseText = result.text;
            
            if (!responseText || responseText.trim() === '') {
                console.error('❌ Empty response detected from Gemini');
                throw new Error('Received empty response from Gemini');
            }

            this.chat = [...(this.chat ?? []), userTurn, { role: 'model', parts: [{ text: responseText }] }];
            
            console.log('✅ Valid response received from Gemini');
            return responseText;
        } catch (error) {
            console.error('❌ Error in GeminiService.sendMessage:', error);
            throw error instanceof Error
                ? error
                : new Error('Failed to get response from Gemini');
        }
    }

    clearChat() {
        console.log('🧹 Clearing chat session');
        this.chat = null;
    }
}

export const createGeminiService = (userId?: string) => {
    return new GeminiService(userId);
};

export const geminiService = new GeminiService();
